/**
 * Camera / Ken Burns motion presets for image clips (preview + export).
 */

export const MOTION_PRESET_CATEGORIES = [
  { id: 'zoom', label: 'Zoom Movements' },
  { id: 'pan', label: 'Pan Movements' },
  { id: 'dolly', label: 'Dolly / Push Movements' },
  { id: 'tracking', label: 'Tracking Movements' },
  { id: 'crane', label: 'Crane / Jib Movements' },
  { id: 'rotation', label: 'Rotation Movements' },
  { id: 'tilt', label: 'Tilt Movements' },
  { id: 'combined', label: 'Combined Movements' },
  { id: 'cinematic', label: 'Cinematic Effects' },
  { id: 'drone', label: 'Drone-Style Shots' },
];

const cat = (id, label, category) => ({ id, label, category });

/** Full catalog — ids are snake_case for export. */
export const MOTION_PRESETS = [
  cat('none', 'None', 'none'),

  cat('zoom_in', 'Zoom In', 'zoom'),
  cat('zoom_out', 'Zoom Out', 'zoom'),
  cat('progressive_zoom_in', 'Progressive Zoom In', 'zoom'),
  cat('progressive_zoom_out', 'Progressive Zoom Out', 'zoom'),
  cat('snap_zoom', 'Snap Zoom', 'zoom'),
  cat('digital_punch_in', 'Digital Punch-In', 'zoom'),
  cat('slow_zoom_in', 'Slow Zoom In', 'zoom'),
  cat('slow_zoom_out', 'Slow Zoom Out', 'zoom'),

  cat('pan_left', 'Pan Left', 'pan'),
  cat('pan_right', 'Pan Right', 'pan'),
  cat('pan_up', 'Pan Up', 'pan'),
  cat('pan_down', 'Pan Down', 'pan'),
  cat('diagonal_pan_up_left', 'Diagonal Pan Up-Left', 'pan'),
  cat('diagonal_pan_up_right', 'Diagonal Pan Up-Right', 'pan'),
  cat('diagonal_pan_down_left', 'Diagonal Pan Down-Left', 'pan'),
  cat('diagonal_pan_down_right', 'Diagonal Pan Down-Right', 'pan'),

  cat('dolly_in', 'Dolly In', 'dolly'),
  cat('dolly_out', 'Dolly Out', 'dolly'),
  cat('push_in', 'Push In', 'dolly'),
  cat('pull_out', 'Pull Out', 'dolly'),

  cat('track_left', 'Track Left', 'tracking'),
  cat('track_right', 'Track Right', 'tracking'),
  cat('follow_subject', 'Follow Subject', 'tracking'),
  cat('side_tracking_shot', 'Side Tracking Shot', 'tracking'),
  cat('reveal_tracking_shot', 'Reveal Tracking Shot', 'tracking'),

  cat('crane_up', 'Crane Up', 'crane'),
  cat('crane_down', 'Crane Down', 'crane'),
  cat('jib_up', 'Jib Up', 'crane'),
  cat('jib_down', 'Jib Down', 'crane'),
  cat('rising_reveal', 'Rising Reveal', 'crane'),
  cat('descending_reveal', 'Descending Reveal', 'crane'),

  cat('orbit_left', 'Orbit Left', 'rotation'),
  cat('orbit_right', 'Orbit Right', 'rotation'),
  cat('orbit_180', '180° Orbit', 'rotation'),
  cat('orbit_360', '360° Orbit', 'rotation'),
  cat('arc_shot', 'Arc Shot', 'rotation'),
  cat('circular_tracking', 'Circular Tracking', 'rotation'),

  cat('tilt_up', 'Tilt Up', 'tilt'),
  cat('tilt_down', 'Tilt Down', 'tilt'),
  cat('tilt_up_reveal', 'Tilt-Up Reveal', 'tilt'),
  cat('tilt_down_reveal', 'Tilt-Down Reveal', 'tilt'),

  cat('zoom_in_pan_left', 'Zoom In + Pan Left', 'combined'),
  cat('zoom_in_pan_right', 'Zoom In + Pan Right', 'combined'),
  cat('zoom_out_pan_up', 'Zoom Out + Pan Up', 'combined'),
  cat('dolly_in_tilt_up', 'Dolly In + Tilt Up', 'combined'),
  cat('orbit_zoom_in', 'Orbit + Zoom In', 'combined'),
  cat('pan_rotation', 'Pan + Rotation', 'combined'),
  cat('crane_zoom', 'Crane + Zoom', 'combined'),

  cat('ken_burns', 'Ken Burns Effect', 'cinematic'),
  cat('parallax_motion', 'Parallax Motion', 'cinematic'),
  cat('handheld_motion', 'Handheld Motion', 'cinematic'),
  cat('floating_camera', 'Floating Camera', 'cinematic'),
  cat('slow_drift', 'Slow Drift', 'cinematic'),
  cat('sway_motion', 'Sway Motion', 'cinematic'),
  cat('push_pull_effect', 'Push-Pull Effect', 'cinematic'),
  cat('whip_pan', 'Whip Pan', 'cinematic'),
  cat('rack_focus_movement', 'Rack Focus Movement', 'cinematic'),

  cat('drone_push_forward', 'Drone Push Forward', 'drone'),
  cat('drone_pull_back', 'Drone Pull Back', 'drone'),
  cat('drone_rise', 'Drone Rise', 'drone'),
  cat('drone_descend', 'Drone Descend', 'drone'),
  cat('birds_eye_orbit', "Bird's-Eye Orbit", 'drone'),
];

/** ~90% of mythology / story-video moves on YouTube. */
export const STORY_VIDEO_MOTION_PRESET_IDS = [
  'slow_zoom_in',
  'slow_zoom_out',
  'pan_left',
  'pan_right',
  'tilt_up',
  'tilt_down',
  'dolly_in',
  'dolly_out',
  'orbit_left',
  'orbit_right',
  'crane_up',
  'crane_down',
  'ken_burns',
  'parallax_motion',
  'slow_drift',
];

const PRESET_BY_ID = Object.fromEntries(MOTION_PRESETS.map((p) => [p.id, p]));

export function normalizeMotionPreset(value) {
  const key = String(value || 'none').trim().toLowerCase().replace(/-/g, '_');
  return PRESET_BY_ID[key] ? key : 'none';
}

export function motionPresetLabel(presetId) {
  return PRESET_BY_ID[normalizeMotionPreset(presetId)]?.label || 'None';
}

export function motionPresetsByCategory() {
  const groups = {};
  MOTION_PRESET_CATEGORIES.forEach((c) => { groups[c.id] = []; });
  MOTION_PRESETS.forEach((p) => {
    if (p.id === 'none') return;
    const bucket = groups[p.category] || groups.cinematic;
    bucket.push(p);
  });
  return MOTION_PRESET_CATEGORIES
    .map((c) => ({ ...c, presets: groups[c.id] || [] }))
    .filter((g) => g.presets.length > 0);
}

export function pickRandomMotionPreset({ storyOnly = false, excludeNone = true } = {}) {
  const pool = storyOnly
    ? STORY_VIDEO_MOTION_PRESET_IDS
    : MOTION_PRESETS.map((p) => p.id).filter((id) => id !== 'none');
  const choices = excludeNone ? pool.filter((id) => id !== 'none') : pool;
  if (!choices.length) return 'ken_burns';
  return choices[Math.floor(Math.random() * choices.length)];
}

export function clampMotionStrength(value, fallback = 0.12) {
  const n = Number(value);
  const s = Number.isFinite(n) ? n : fallback;
  return Math.max(0.01, Math.min(0.45, s));
}

function clamp01(p) {
  return Math.max(0, Math.min(1, Number(p) || 0));
}

function smoothstep(p) {
  return p * p * (3 - 2 * p);
}

function easeIn(p) {
  return p * p;
}

function easeOut(p) {
  return 1 - (1 - p) * (1 - p);
}

function snapProgress(p) {
  if (p < 0.82) return 0;
  return (p - 0.82) / 0.18;
}

function wobble(p, amp = 1) {
  return Math.sin(p * Math.PI * 4) * 0.015 * amp;
}

/**
 * @returns {{ scale: number, tx: number, ty: number, rotate: number }}
 */
export function computeMotionTransform(presetId, progress, strength) {
  const key = normalizeMotionPreset(presetId);
  const p = clamp01(progress);
  const s = clampMotionStrength(strength);
  const sp = smoothstep(p);
  const lerp = (a, b, t = p) => a + (b - a) * t;

  if (key === 'none') {
    return { scale: 1, tx: 0, ty: 0, rotate: 0 };
  }

  const panScale = 1 + s * 0.35;
  const slow = s * 0.65;
  const drift = 0.04 + s * 0.08;

  switch (key) {
    case 'zoom_in':
    case 'dolly_in':
    case 'push_in':
    case 'drone_push_forward':
      return { scale: lerp(1, 1 + s), tx: 0, ty: 0, rotate: 0 };
    case 'zoom_out':
    case 'dolly_out':
    case 'pull_out':
    case 'drone_pull_back':
      return { scale: lerp(1 + s, 1), tx: 0, ty: 0, rotate: 0 };
    case 'progressive_zoom_in':
      return { scale: lerp(1, 1 + s, easeIn(p)), tx: 0, ty: 0, rotate: 0 };
    case 'progressive_zoom_out':
      return { scale: lerp(1 + s, 1, easeOut(p)), tx: 0, ty: 0, rotate: 0 };
    case 'snap_zoom':
    case 'digital_punch_in':
      return { scale: lerp(1, 1 + s * 1.15, snapProgress(p)), tx: 0, ty: 0, rotate: 0 };
    case 'slow_zoom_in':
      return { scale: lerp(1, 1 + slow), tx: 0, ty: 0, rotate: 0 };
    case 'slow_zoom_out':
      return { scale: lerp(1 + slow, 1), tx: 0, ty: 0, rotate: 0 };

    case 'pan_left':
    case 'track_left':
      return { scale: panScale, tx: lerp(drift, -drift), ty: 0, rotate: 0 };
    case 'pan_right':
    case 'track_right':
      return { scale: panScale, tx: lerp(-drift, drift), ty: 0, rotate: 0 };
    case 'pan_up':
    case 'tilt_up':
    case 'jib_up':
      return { scale: panScale, tx: 0, ty: lerp(drift, -drift), rotate: 0 };
    case 'pan_down':
    case 'tilt_down':
    case 'jib_down':
      return { scale: panScale, tx: 0, ty: lerp(-drift, drift), rotate: 0 };
    case 'diagonal_pan_up_left':
      return { scale: panScale, tx: lerp(drift, -drift), ty: lerp(drift, -drift), rotate: 0 };
    case 'diagonal_pan_up_right':
      return { scale: panScale, tx: lerp(-drift, drift), ty: lerp(drift, -drift), rotate: 0 };
    case 'diagonal_pan_down_left':
      return { scale: panScale, tx: lerp(drift, -drift), ty: lerp(-drift, drift), rotate: 0 };
    case 'diagonal_pan_down_right':
      return { scale: panScale, tx: lerp(-drift, drift), ty: lerp(-drift, drift), rotate: 0 };

    case 'follow_subject':
    case 'side_tracking_shot':
      return { scale: panScale, tx: lerp(-drift * 0.6, drift * 0.6), ty: wobble(p), rotate: 0 };
    case 'reveal_tracking_shot':
      return { scale: lerp(1 + s * 0.2, 1 + s), tx: lerp(-drift, drift * 0.5), ty: 0, rotate: 0 };

    case 'crane_up':
    case 'drone_rise':
    case 'rising_reveal':
      return { scale: lerp(1, 1 + s * 0.5), tx: 0, ty: lerp(drift * 0.5, -drift), rotate: 0 };
    case 'crane_down':
    case 'drone_descend':
    case 'descending_reveal':
      return { scale: lerp(1 + s * 0.35, 1), tx: 0, ty: lerp(-drift, drift * 0.5), rotate: 0 };

    case 'orbit_left':
    case 'arc_shot':
      return { scale: 1 + s * 0.25, tx: lerp(drift, -drift), ty: 0, rotate: lerp(2.5, -2.5) };
    case 'orbit_right':
      return { scale: 1 + s * 0.25, tx: lerp(-drift, drift), ty: 0, rotate: lerp(-2.5, 2.5) };
    case 'orbit_180':
      return { scale: 1 + s * 0.3, tx: lerp(drift, -drift), ty: 0, rotate: lerp(4, -4) };
    case 'orbit_360':
    case 'circular_tracking':
    case 'birds_eye_orbit':
      return { scale: 1 + s * 0.35, tx: Math.sin(p * Math.PI * 2) * drift, ty: Math.cos(p * Math.PI * 2) * drift * 0.5, rotate: lerp(0, 6) };

    case 'tilt_up_reveal':
      return { scale: lerp(1, 1 + s * 0.6), tx: 0, ty: lerp(drift, -drift * 1.2), rotate: lerp(-1, 0) };
    case 'tilt_down_reveal':
      return { scale: lerp(1 + s * 0.4, 1), tx: 0, ty: lerp(-drift, drift), rotate: lerp(0, 1) };

    case 'zoom_in_pan_left':
      return { scale: lerp(1, 1 + s), tx: lerp(drift, -drift), ty: 0, rotate: 0 };
    case 'zoom_in_pan_right':
      return { scale: lerp(1, 1 + s), tx: lerp(-drift, drift), ty: 0, rotate: 0 };
    case 'zoom_out_pan_up':
      return { scale: lerp(1 + s, 1), tx: 0, ty: lerp(drift, -drift), rotate: 0 };
    case 'dolly_in_tilt_up':
      return { scale: lerp(1, 1 + s), tx: 0, ty: lerp(drift * 0.5, -drift), rotate: lerp(-1.5, 0) };
    case 'orbit_zoom_in':
      return { scale: lerp(1, 1 + s), tx: lerp(-drift, drift), ty: 0, rotate: lerp(-2, 2) };
    case 'pan_rotation':
      return { scale: panScale, tx: lerp(-drift, drift), ty: 0, rotate: lerp(-3, 3) };
    case 'crane_zoom':
      return { scale: lerp(1, 1 + s * 0.85), tx: 0, ty: lerp(drift, -drift * 0.8), rotate: 0 };

    case 'ken_burns':
      return { scale: lerp(1, 1 + s), tx: lerp(-drift * 0.8, drift * 0.8), ty: lerp(drift * 0.6, -drift * 0.6), rotate: 0 };
    case 'parallax_motion':
      return { scale: 1 + s * 0.2, tx: lerp(-drift * 1.2, drift * 1.2), ty: lerp(drift * 0.4, -drift * 0.4), rotate: 0 };
    case 'handheld_motion':
      return { scale: 1 + s * 0.08, tx: wobble(p, 2), ty: wobble(p + 0.25, 2), rotate: wobble(p, 40) };
    case 'floating_camera':
    case 'slow_drift':
      return { scale: 1 + s * 0.12, tx: Math.sin(p * Math.PI * 2) * drift * 0.35, ty: Math.cos(p * Math.PI * 2) * drift * 0.25, rotate: 0 };
    case 'sway_motion':
      return { scale: 1 + s * 0.1, tx: Math.sin(p * Math.PI * 3) * drift * 0.5, ty: 0, rotate: Math.sin(p * Math.PI * 2) * 1.2 };
    case 'push_pull_effect':
      return { scale: lerp(1, 1 + s, sp) * (1 + Math.sin(p * Math.PI) * 0.04), tx: 0, ty: 0, rotate: 0 };
    case 'whip_pan':
      return { scale: panScale, tx: lerp(-drift * 2, drift * 2, easeIn(p)), ty: 0, rotate: lerp(-4, 4) };
    case 'rack_focus_movement':
      return { scale: lerp(1 + s * 0.15, 1 + s * 0.45, sp), tx: lerp(0, drift * 0.3), ty: 0, rotate: 0 };

    default:
      return { scale: 1, tx: 0, ty: 0, rotate: 0 };
  }
}

export function motionPreviewStyleFromPreset(presetId, progress, strength) {
  const { scale, tx, ty, rotate } = computeMotionTransform(presetId, progress, strength);
  if (normalizeMotionPreset(presetId) === 'none') return {};
  return {
    transform: `translate3d(${tx * 100}%, ${ty * 100}%, 0) scale(${scale}) rotate(${rotate}deg)`,
    transformOrigin: 'center',
    willChange: 'transform',
  };
}

export const VALID_MOTION_PRESET_IDS = MOTION_PRESETS.map((p) => p.id);
